import React from 'react'

import { Controller, UseFormReturn } from 'react-hook-form'

import { RegisterRequestDTO } from '@/core/api/dtos/request/auth/register'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { ErrorMessage } from '@/shared/components/atoms/ErrorMessage'
import { Label } from '@/shared/components/atoms/Label/Label'
import { PhoneInput } from '@/shared/components/atoms/PhoneNumber'
import { CountrySelector } from '@/shared/components/molecules/CountrySelector/CountrySelector'

interface RegisterStep3Props {
	form: UseFormReturn<RegisterRequestDTO>
	isLoading: boolean
	onNextStep: (formData: Partial<RegisterRequestDTO>) => void
	onPrevStep: () => void
}

export const RegisterStep3: React.FC<RegisterStep3Props> = ({
	form,
	isLoading,
	onNextStep,
	onPrevStep,
}) => {
	const { t } = useTranslation('auth')

	const handleNextStep = async () => {
		const isValid = await form.trigger(['phone_number', 'country'])

		if (isValid) {
			const { phone_number, country } = form.getValues()
			const stepFormData = {
				phone_number,
				country,
			}
			onNextStep(stepFormData)
		}
	}

	const phoneNumber = form.watch('phone_number') || ''
	const country = form.watch('country')

	const isFormValid =
		phoneNumber.length > 0 &&
		!!country &&
		Object.keys(form.formState.errors).length === 0

	return (
		<div className="space-y-6">
			<h1 className="text-2xl font-bold text-center mb-4">
				{t('register.profileTitle')}
			</h1>

			<p className="text-center mb-8">{t('register.profileInstructions')}</p>

			<div className="space-y-4">
				<div>
					<Label className="mb-2 block">{t('register.phoneNumber')}</Label>
					<Controller
						control={form.control}
						name="phone_number"
						render={({ field, fieldState: { error } }) => (
							<div>
								<PhoneInput
									placeholder={t('register.phoneNumber')}
									className="h-10"
									value={field.value}
									onChange={(value) => {
										field.onChange(value)
									}}
									onCountryChange={(country) => {
										if (country) {
											form.setValue('country', country, {
												shouldValidate: true,
												shouldDirty: true,
												shouldTouch: true,
											})
										}
									}}
								/>
								{error && <ErrorMessage error={error.message ?? ''} />}
							</div>
						)}
					/>
				</div>

				<div>
					<Label className="mb-2 block">{t('register.country')}</Label>
					<Controller
						control={form.control}
						name="country"
						render={({ field, fieldState: { error } }) => (
							<div>
								<CountrySelector
									value={field.value}
									onChange={field.onChange}
									placeholder={t('register.country')}
									className="h-10"
								/>
								{error && <ErrorMessage error={error.message ?? ''} />}
							</div>
						)}
					/>
				</div>
			</div>

			<div className="mt-8 flex gap-4">
				<Button
					variant="ghost"
					onClick={onPrevStep}
					className="px-8 py-3 cursor-pointer flex-1 h-10 text-sm font-medium"
					disabled={isLoading}
				>
					{t('register.back')}
				</Button>
				<Button
					onClick={handleNextStep}
					className="px-8 py-3 cursor-pointer flex-1 h-10 text-sm font-medium"
					disabled={isLoading || !isFormValid}
				>
					{isLoading ? 'Loading...' : t('register.continue')}
				</Button>
			</div>
		</div>
	)
}
